import React, { useEffect, useRef, useState } from 'react';
import EventIcon, { ICONS, iconNames, iconOf, withIcon, ICON_HEADING } from './EventIcon';

/**
 * The icon chooser in the header of the event form.
 *
 * It used to be eight pictures in a row, and eight was already the most that
 * would fit beside the back arrow on a narrow phone. Silvia kept twenty-four,
 * which a row cannot hold — so it became what she asked for: a drop-down built
 * like the language menu, the drawing where the flag would be and the name
 * beside it. A list of twenty-four pictures with no words is a list of nothing
 * to somebody who does not already know what each one means, and to anybody
 * using a screen reader it is worse than nothing.
 *
 * The trigger stays 38px — the size of the back arrow — so the header still
 * reads as one line of equal things.
 *
 * What is chosen goes to the FRONT OF THE TITLE as an emoji, and MeMo draws it
 * as a picture wherever it shows it. See EventIcon.jsx for why both halves are
 * needed.
 */

const NONE = {
  it: 'Nessuna', en: 'None', es: 'Ninguno', de: 'Keins', fr: 'Aucune', nl: 'Geen',
  pt: 'Nenhum', 'pt-BR': 'Nenhum', ru: 'Без значка', pl: 'Brak', ro: 'Niciuna',
  zh: '不使用', ja: 'なし', hi: 'कोई नहीं', ko: '없음', ar: 'بدون',
};

function pick(table, language) {
  const raw = String(language || 'it');
  return table[raw] || table[raw.split('-')[0]] || table.en;
}

export default function IconPicker({ value, onChange, language, disabled }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const names = iconNames(language);
  const heading = pick(ICON_HEADING, language);
  const noneWord = pick(NONE, language);
  const current = iconOf(value);
  const currentName = current ? names[ICONS.indexOf(current)] : '';

  // A menu that will not close is a trap: outside tap and Escape both shut it.
  useEffect(() => {
    if (!open) return undefined;
    const away = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  function choose(icon) {
    onChange(withIcon(value, icon));
    setOpen(false);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={currentName ? `${heading}: ${currentName}` : heading}
        title={currentName || heading}
        onClick={() => setOpen(o => !o)}
        style={{
          height: 38, minWidth: 38, padding: current ? '0 8px' : 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          borderRadius: 'var(--radius-sm)',
          border: `1.5px solid ${open || current ? 'var(--color-primary)' : 'var(--color-border)'}`,
          background: current ? 'var(--color-primary-light)' : 'transparent',
          color: 'var(--color-text)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'border-color 0.15s, background 0.15s',
        }}
      >
        {current
          ? <EventIcon icon={current} size={20} />
          : /* Nothing chosen yet: a plain tag, so the button says "there is
               something to pick here" instead of standing empty. */
            <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true"
                 stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.6 13.2l-7.4 7.4a2 2 0 0 1-2.8 0l-7-7A2 2 0 0 1 3 12.2V5a2 2 0 0 1 2-2h7.2a2 2 0 0 1 1.4.6l7 7a2 2 0 0 1 0 2.8z"/>
              <circle cx="7.6" cy="7.6" r="1.3"/>
            </svg>}
        <span aria-hidden="true" style={{ fontSize: '0.62rem', lineHeight: 1, color: 'var(--color-text-muted)' }}>
          {open ? '▲' : '▾'}
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={heading}
          style={{
            position: 'absolute', insetInlineStart: 0, top: 44, zIndex: 60,
            // Never wider than the screen — this opens on a phone, and a menu
            // that pushes the page sideways is the bug Cecilia reported.
            width: 'min(330px, calc(100vw - 28px))',
            maxHeight: 320, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            background: 'var(--color-card)',
            border: '1.5px solid var(--color-border)',
            borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            padding: 8,
          }}
        >
          <button
            type="button"
            role="option"
            aria-selected={!current}
            onClick={() => choose('')}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              minHeight: 40, padding: '8px 10px', marginBottom: 6,
              fontFamily: 'inherit', fontSize: '0.88rem',
              fontWeight: !current ? 800 : 500,
              textAlign: 'start', cursor: 'pointer', borderRadius: 10,
              background: !current ? 'var(--color-primary-light)' : 'transparent',
              color: 'var(--color-text-muted)',
              border: `1.5px solid ${!current ? 'var(--color-primary)' : 'var(--color-border)'}`,
            }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"
                 stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round">
              <path d="M5 19L19 5"/>
            </svg>
            <span>{noneWord}</span>
          </button>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 6 }}>
            {ICONS.map((icon, i) => {
              const active = current === icon;
              return (
                <button
                  key={icon}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => choose(icon)}
                  className="tap-grow"
                  style={{
                    '--tap-x': '4px',
                    display: 'flex', alignItems: 'center', gap: 8,
                    minHeight: 44, padding: '8px 10px',
                    fontFamily: 'inherit', fontSize: '0.88rem',
                    fontWeight: active ? 800 : 500,
                    textAlign: 'start', cursor: 'pointer', borderRadius: 10,
                    background: active ? 'var(--color-primary-light)' : 'var(--color-card)',
                    color: 'var(--color-text)',
                    border: active ? '2px solid var(--color-primary)' : '1.5px solid var(--color-border)',
                  }}
                >
                  <EventIcon icon={icon} size={20} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{names[i]}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
